import React, { useState, useEffect, useRef } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNavigate, useParams } from 'react-router-dom';
import { get, put } from 'aws-amplify/api';

function EditGuestScreen() {
  const { eventId, guestId } = useParams();
  const navigate = useNavigate();
  
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tag, setTag] = useState('');
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [rsvpStatus, setRsvpStatus] = useState('Unconfirmed');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const tagInputRef = useRef(null);

  useEffect(() => {
    const fetchGuestData = async () => {
      setIsLoading(true);
      try {
        const restOperation = get({
          apiName: 'events',
          path: `/guests/${eventId}/${guestId}`,
        });
        await restOperation.response;
        const { body } = await restOperation.response;
        const guest = await body.json();
        
        // Populate form with guest data
        setFirstName(guest.first_name || '');
        setLastName(guest.last_name || '');
        setEmail(guest.email || '');
        setPhone(guest.phone || '');
        setRsvpStatus(guest.rsvp_status || 'Unconfirmed');
        setTags(guest.tags || []);
      } catch (error) {
        console.error('Error fetching guest details:', error);
        setError('Failed to load guest details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGuestData();
  }, [eventId, guestId]);

  const handleUpdateGuest = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!firstName || !lastName) {
      setError('First and last name are required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const updatedGuest = {
        event_id: eventId,
        guest_id: guestId,
        first_name: firstName,
        last_name: lastName,
        email: email || null,
        phone: phone || null,
        rsvp_status: rsvpStatus
      };

      console.log('Sending PUT:', JSON.stringify(updatedGuest));
      const restOperation = put({
        apiName: 'events',
        path: `/guests/${guestId}`,
        options: {
          body: JSON.stringify(updatedGuest),
          headers: {
            'Content-Type': 'application/json'
          }
        }
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();
      console.log('Guest updated:', json);
      
      // Navigate back to guest list
      navigate(`/guests/${eventId}`, { state: { updatedGuest: json } });
    } catch (error) {
      console.error('Error updating guest:', error);
      setError('Failed to update guest. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to fetch event tags
  const fetchEventTags = async () => {
    if (isLoadingTags) return;
    
    try {
      setIsLoadingTags(true);
      const restOperation = get({
        apiName: 'events',
        path: `/guests/${eventId}/tags`,
        withCredentials: true
      });
      await restOperation.response;
      const { body } = await restOperation.response;
      const json = await body.json();
      console.log('Got tags response:', json);
      setAvailableTags(Array.isArray(json) ? json : []);
    } catch (error) {
      console.error('Error fetching tags:', error);
    } finally {
      setIsLoadingTags(false);
    }
  };
  
  // Close tag suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagInputRef.current && !tagInputRef.current.contains(event.target)) {
        setShowTagSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tagInputRef]);
  
  if (isLoading) {
    return <LoadingSpinner message="Loading guest details..." />;
  }

  return (
    <div className="App-container">
      <h1 className="App-heading-colored">Edit Guest</h1>
      
      {error && (
        <div className="App-error">
          {error}
        </div>
      )}
      
      <form onSubmit={handleUpdateGuest} className="App-form">
        <div>
          <label htmlFor="firstName" className="App-label">
            First Name *
          </label>
          <input
            type="text"
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="App-input"
            required
          />
        </div>
        
        <div>
          <label htmlFor="lastName" className="App-label">
            Last Name *
          </label>
          <input
            type="text"
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="App-input"
            required
          />
        </div>
        
        <div>
          <label htmlFor="email" className="App-label">
            Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="App-input"
          />
        </div>
        
        <div>
          <label htmlFor="phone" className="App-label">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="App-input"
          />
        </div>

        <div>
          <label htmlFor="tag" className="App-label">
            Tag
          </label>
          <div className="relative">
            <input
              type="text"
              id="tag"
              ref={tagInputRef}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onFocus={() => {
                setShowTagSuggestions(true);
                // Only fetch tags when the field is clicked
                fetchEventTags();
              }}
              className="App-input"
            />
            {showTagSuggestions && availableTags.length > 0 && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded shadow-lg mt-1">
                {availableTags
                  .filter(t => t.toLowerCase().includes(tag.toLowerCase()))
                  .map((suggestedTag, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setTag("");
                        if (!tags.includes(suggestedTag)) {
                          setTags([...tags, suggestedTag]);
                        }
                        setShowTagSuggestions(false);
                      }}
                    >
                      {suggestedTag}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map((t, index) => (
              <div 
                key={index} 
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm flex items-center gap-1"
              >
                {t}
                <button
                  type="button"
                  onClick={() => {
                    setTags(tags.filter((_, i) => i !== index));
                  }}
                  className="text-blue-500 hover:text-blue-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <div className="mt-2">
            <button
              type="button"
              className="text-sm text-blue-500 hover:text-blue-700"
              onClick={() => {
                if (tag.trim() && !tags.includes(tag.trim())) {
                  setTags([...tags, tag.trim()]);
                  setTag("");
                  tagInputRef.current.focus();
                }
              }}
            >
              Add Tag
            </button>
          </div>
        </div>
        
        <div>
          <label htmlFor="rsvpStatus" className="App-label">
            RSVP Status
          </label>
          <select
            id="rsvpStatus"
            value={rsvpStatus}
            onChange={(e) => setRsvpStatus(e.target.value)}
            className="App-input"
          >
            <option value="Confirmed">Confirmed</option>
            <option value="Declined">Declined</option>
            <option value="Viewed">Viewed</option>
            <option value="Unconfirmed">Unconfirmed</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        
        <div className="App-actions App-margin-top">
          <button
            type="button"
            onClick={() => navigate(`/guests/${eventId}`)}
            className="button button--secondary"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="button button--secondary"
          >
            {isSubmitting ? 'Updating...' : 'Update Guest'}
          </button>
        </div>
      </form>
      
      {isSubmitting && <LoadingSpinner message="Updating guest..." />}
    </div>
  );
}

export default EditGuestScreen;